// Marks every request the dashboard sends to the fastn API as a dashboard
// session, so the gateway lets it follow the org picker.
//
// The Keycloak client alone cannot say that: prod lets `fastn-app` mint tokens
// by username/password, so a script's token looks exactly like this tab's, and
// without the marker it would follow whatever org was last picked here. The
// gateway treats an unmarked fastn-app request as an API session and resolves
// it to the user's home org (services/api/src/lib/gateway-org.ts).
//
// Done by wrapping window.fetch rather than in authFetch because several
// callers (the AI SDK chat transport, a few raw fetches) never go through
// authFetch, and a request that misses the marker silently lands in the home
// org instead of the org on screen. Only our own API is marked: Keycloak, the
// OAuth proxy and third parties would fail CORS preflight on an unknown header.

import { API_BASE } from "./utils";

export const DASHBOARD_MARKER_HEADER = "x-fastn-dashboard";

// /v1 and /v2 are the public spelling of /api/v1 and /api/v2 (KAT-8371,
// docs/services/api-prefix.md). A call made under either must carry the marker.
const API_PATH = /^\/(api|v1|v2)\//;

export function isFastnApiUrl(url: string, apiBase: string, pageHref: string): boolean {
  let target: URL;
  let base: URL;
  try {
    target = new URL(url, pageHref);
    base = new URL(apiBase || pageHref, pageHref);
  } catch {
    return false;
  }
  if (target.origin !== base.origin) return false;
  // An API base with a path of its own (https://live.fastn.ai/gcp on the /gcp/
  // edge) puts that path in front of every call, so match what follows it.
  const prefix = apiBase ? base.pathname.replace(/\/+$/, "") : "";
  if (!prefix) return API_PATH.test(target.pathname);
  return target.pathname.startsWith(`${prefix}/`) && API_PATH.test(target.pathname.slice(prefix.length));
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

export function markDashboardRequest(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): [RequestInfo | URL, RequestInit | undefined] {
  if (typeof Request !== "undefined" && input instanceof Request && !init?.headers) {
    const headers = new Headers(input.headers);
    headers.set(DASHBOARD_MARKER_HEADER, "1");
    return [new Request(input, { headers }), init];
  }
  const headers = new Headers(init?.headers);
  headers.set(DASHBOARD_MARKER_HEADER, "1");
  return [input, { ...init, headers }];
}

let installed = false;

export function initDashboardSessionMarker(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (!isFastnApiUrl(requestUrl(input), API_BASE, window.location.href)) return originalFetch(input, init);
    const [markedInput, markedInit] = markDashboardRequest(input, init);
    return originalFetch(markedInput, markedInit);
  };
}
